import {
  BoundingSphere,
  Cesium3DTileFeature,
  Cesium3DTileStyle,
  Cesium3DTileset,
  Color,
  Entity,
  Model,
  createGuid,
  defined,
} from "@cesium/engine";
import PointEntity from "../edit/PointEntity";
import MeasurementAltitude from "../measurement/MeasurementAltitude";
import LinestringEntity from "../edit/LinestringEntity";
import MeasurementLengthStraight from "../measurement/MeasurementLengthStraight";
import MeasurementLengthGround from "../measurement/MeasurementLengthGround";
import MeasurementLengthPlane from "../measurement/MeasurementLengthPlane";
import PolygonEntity from "../edit/PolygonEntity";
import MeasurementArea from "../measurement/MeasurementArea";
import MeasurementVolume from "../measurement/MeasurementVolume";
import MeasurementLengthVertical from "../measurement/MeasurementLengthVertical";
import MeasurementLengthHorizontal from "../measurement/MeasurementLengthHorizontal";
import AnalysisVisibility from "../analysis/AnalysisVisibility";

const EditUtils = {
  /** 객체 색상 변경. <br>
   * Change the color of the object.
   * @param {object} object 색상 변경할 객체.<br>objects to Change Color.
   * @param {Color} color 변경할 색상.<br>Color to change.
   */
  changeColor(pickedObject, color) {
    try {
      if (pickedObject.primitive instanceof Model) {
        this.changeModelColor(pickedObject.primitive, color);
      } else if (this.isCesium3DTileset(pickedObject)) {
        this.changeTilesetColor(pickedObject, color);
      } else if (pickedObject?.color && defined(pickedObject?.color)) {
        pickedObject.color = color;
      }
    } catch (error) {
      console.error("선택된 객체가 없습니다.");
    }
  },

  isModel(object) {
    return object instanceof Model || object.primitive instanceof Model;
  },

  isCesium3DTileset(object) {
    if (object instanceof Cesium3DTileFeature) {
      return false;
    }
    const tilesetObject = object.primitive || object.tileset || object;
    return tilesetObject instanceof Cesium3DTileset;
  },

  getCesium3DTileset(object) {
    if (this.isCesium3DTileset(object)) {
      return object.primitive || object.tileset || object;
    }
  },
  changeModelColor(model, color) {
    model.color = color;
  },

  changeTilesetColor(tileset, color) {
    const tilesetToColor = tileset.primitive || tileset.tileset || tileset;
    tilesetToColor.style = new Cesium3DTileStyle({ color: color });
    tilesetToColor.style = undefined;
  },
  /**
   * 객체의 기존 색상으로 변경.<br>
   * Set Original material.
   * @param {object} objects 색상 변경할 객체.<br>objects to Change Color.
   */
  setOriginalColor(pickedObject) {
    if (pickedObject.originalColor) {
      return;
    }
    let mainObject =
      pickedObject.primitive || pickedObject.tileset || pickedObject;
    if (pickedObject instanceof Cesium3DTileFeature) {
      mainObject = pickedObject;
    }

    if (this.isCesium3DTileset(mainObject)) {
      pickedObject.originalColor = 'color("rgba(255, 255, 255, 1)")';
    } else if (mainObject.color) {
      pickedObject.originalColor = Color.clone(mainObject.color);
    } else {
      pickedObject.originalColor = new Color();
    }
  },

  /**
   * 객체의 기존 색상 조회.<br>
   * Get Original material.
   * @param {object} objects 색상을 조회할 객체.<br>objects to Change Color.
   * @returns {Color} 기존 색상.<br>The color before it changed
   */
  getOriginalColor(pickedObject) {
    return pickedObject.originalColor;
  },

  getColorByObjectType(pickedObject, color) {
    if (this.isCesium3DTileset(pickedObject)) {
      const red = color.red * 255;
      const green = color.green * 255;
      const blue = color.blue * 255;
      const alpha = color.alpha;

      return `color("rgba(${red}, ${green}, ${blue}, ${alpha})")`;
    }
    return color;
  },

  isPoint(viewer, pickedObject) {
    const ditapEntity = viewer.ditapStorage.getDitapEntity(pickedObject);
    if (
      (ditapEntity && ditapEntity instanceof PointEntity) ||
      ditapEntity instanceof MeasurementAltitude ||
      pickedObject?.primitive?.position ||
      pickedObject?.id?.position?.getValue(new Date())
    ) {
      return true;
    }
    return false;
  },

  isPolyline(viewer, pickedObject) {
    const ditapEntity = viewer.ditapStorage.getDitapEntity(pickedObject);
    if (
      (ditapEntity && ditapEntity instanceof LinestringEntity) ||
      ditapEntity instanceof MeasurementLengthStraight ||
      ditapEntity instanceof MeasurementLengthGround ||
      ditapEntity instanceof MeasurementLengthPlane ||
      ditapEntity instanceof MeasurementLengthVertical ||
      ditapEntity instanceof MeasurementLengthHorizontal ||
      pickedObject?.id?.polyline?.positions?.getValue()
    ) {
      return true;
    }
    return false;
  },

  isPolygon(viewer, pickedObject) {
    const ditapEntity = viewer.ditapStorage.getDitapEntity(pickedObject);
    if (
      (ditapEntity && ditapEntity instanceof PolygonEntity) ||
      ditapEntity instanceof MeasurementArea ||
      ditapEntity instanceof MeasurementVolume ||
      pickedObject?.id?.polygon?.hierarchy?.getValue()?.positions ||
      pickedObject?.primitive?.geometryInstances?.geometry?._polygonHierarchy
        ?.positions
    ) {
      return true;
    }
    return false;
  },

  getObjectPositions(viewer, pickedObject) {
    const ditapEntity = viewer.ditapStorage.getDitapEntity(pickedObject);
    let positions = [];

    const entityPositions =
      pickedObject?.primitive?.position ||
      pickedObject?.id?.position?.getValue(new Date()) ||
      pickedObject?.id?.polyline?.positions?.getValue() ||
      pickedObject?.id?.polygon?.hierarchy?.getValue()?.positions ||
      pickedObject?.primitive?.geometryInstances?.geometry?._polygonHierarchy
        ?.positions;

    if (ditapEntity) {
      // 뷰어에 저장된 엔티티인 경우
      if (ditapEntity instanceof AnalysisVisibility) {
        positions = [ditapEntity.frustum.position.getValue(new Date())];
      } else if (ditapEntity instanceof MeasurementAltitude) {
        positions = [ditapEntity.position];
      } else if (
        ditapEntity instanceof PolygonEntity ||
        ditapEntity instanceof LinestringEntity ||
        ditapEntity instanceof PointEntity
      ) {
        positions = ditapEntity.computePositions();
      } else {
        positions = ditapEntity.positions;
      }
    } else if (entityPositions) {
      positions = Array.isArray(entityPositions)
        ? entityPositions
        : [entityPositions];
    } else if (pickedObject.primitive?.boundingSphere?.center) {
      positions = [pickedObject.primitive?.boundingSphere?.center];
    }
    return positions;
  },

  /*
   * 객체의 Cartesian 배열 반환.</br>
   * Returns the Cartesian array of an object
   * @param {object} object Cartesian 배열을 반환할 객체.</br> Object to return Cartesian array
   * @return {Cartesian3[]} 객체의 중심 Cartesian 배열.</br>  The center cartesian array of the object
   */
  getObjectCenterPosition(viewer, pickedObject) {
    // * CARTESIAN3 - 엔티티의 좌표 배열을 반환하는 함수
    // 뷰어에 저장된 엔티티
    const ditapEntity = viewer.ditapStorage.getDitapEntity(pickedObject);
    let positions = [];

    const entityPositions =
      pickedObject?.primitive?.position ||
      pickedObject?.id?.position?.getValue(new Date()) ||
      pickedObject?.id?.polyline?.positions?.getValue() ||
      pickedObject?.id?.polygon?.hierarchy?.getValue()?.positions ||
      pickedObject?.primitive?.geometryInstances?.geometry?._polygonHierarchy
        ?.positions;

    if (ditapEntity) {
      // 뷰어에 저장된 엔티티인 경우
      if (ditapEntity instanceof AnalysisVisibility) {
        positions = [ditapEntity.frustum.position.getValue(new Date())];
      } else if (ditapEntity instanceof MeasurementAltitude) {
        positions = [ditapEntity.position];
      } else if (
        ditapEntity instanceof PolygonEntity ||
        ditapEntity instanceof LinestringEntity ||
        ditapEntity instanceof PointEntity
      ) {
        positions = [ditapEntity.position];
      } else {
        positions = ditapEntity.positions;
      }
    } else if (entityPositions) {
      // 1. 엔티티인 경우
      // point인 경우:
      // pickedObject.id.position.getValue(new Date());
      // pickedObject.primitive.position
      // polygon인 경우:
      // pickedObject.id.polygon.hierarchy.getValue().positions;
      // polyline인 경우:
      // pickedObject.id.polyline.positions.getValue()
      positions = Array.isArray(entityPositions)
        ? entityPositions
        : [entityPositions];
      // 뷰어에 저장된 엔티티가 아닌 경우
    } else if (pickedObject.primitive?.boundingSphere?.center) {
      // 2. primitive인 경우
      // 3. model인 경우
      // 4. 3d tileset인 경우
      // pickedObject.primitive.boundingSphere.center
      positions = [pickedObject.primitive?.boundingSphere?.center];
    }
    return positions;
  },

  /*
   * 선택된 객체의 아이디 반환 및 생성.<br>
   * Gets the ID of the selected object and creates an ID if there is no ID.
   * @param {object} picked 선택된 객체.<br>The seleted object.
   * @returns {string} 아이디.<br>Picked Id of 3DTileset.
   */
  getSelectObjectId(viewer, picked) {
    // * OBJECT - 선택된 객체의 아이디를 리턴하고 없는 경우 아이디를 생성해 주는 함수
    // 뷰어에 저장된 엔티티
    const ditapEntity = viewer.ditapStorage.getDitapEntity(picked);
    let pickedId = createGuid();

    if (ditapEntity) {
      pickedId = ditapEntity.id || (ditapEntity.id = pickedId);
    } else if (picked instanceof Cesium3DTileFeature) {
      const tileProperty = this.get3DTileProperty(picked);
      pickedId = tileProperty.id || tileProperty.ID || (picked.id = pickedId);
    } else if (picked.primitive instanceof Model) {
      pickedId = picked.id || (picked.id = pickedId);
    } else if (picked.id instanceof Entity) {
      pickedId = picked.id.id || (picked.id.id = pickedId);
    }

    return pickedId;
  },

  /*
   * 3d 타일셋 속성 조회.</br>
   * Get all properties of 3DTileset.
   * @param {object} feature 선택된 3d타일셋.</br> The selected 3DTileset object.
   * @returns {object} 3d 타일셋 속성.</br>The properties of 3DTileset
   */
  get3DTileProperty(feature) {
    // * 3DTILESET - 3DTileset의 모든 속성을 리턴하는 함수
    const propertyIds = feature.getPropertyIds();
    const property = {};

    propertyIds.forEach((propertyId) => {
      const value = feature.getProperty(propertyId);
      const reg = /[\{\}\[\]\/?.,;:|\)*~`!^\-_+<>@\#$%&\\\=\(\'\"]/gi;
      // const key = (propertyId + "")
      const key = propertyId
        .toUpperCase()
        .replace("CESIUM", "")
        .replace(reg, "");
      if (defined(value)) {
        property[key] = value;
      }
    });

    // id 추가
    property["ID"] = this.get3DTileName(feature);

    return property;
  },

  /*
   * 카메라 시점으로 대상 이동.</br>
   * Move camera perspective to target.
   * @param {object} object 이동할 카메라 시점 객체.</br> Camera Point of View Object to Move.
   */
  zoomToObject(viewer, pickedObject) {
    let boundingSphere;
    if (pickedObject instanceof Cesium3DTileFeature) {
      return;
    }
    const entityPositions = this.getObjectCenterPosition(viewer, pickedObject);
    const getDitapEntity = viewer.ditapStorage.getDitapEntity(pickedObject);
    if (
      getDitapEntity instanceof PointEntity ||
      getDitapEntity instanceof PolygonEntity ||
      getDitapEntity instanceof LinestringEntity
    ) {
      boundingSphere = getDitapEntity.computeBoundingSphere();
    } else if (getDitapEntity instanceof AnalysisVisibility) {
      const visibility = getDitapEntity;
      const center = visibility.frustum.position.getValue(new Date());

      const radius = visibility.distance;

      boundingSphere = new BoundingSphere(
        { x: center.x, y: center.y, z: center.z },
        radius
      );
    } else if (entityPositions) {
      const boundingSpherePositions = [];

      entityPositions.forEach((position) => {
        boundingSpherePositions.push(position.x);
        boundingSpherePositions.push(position.y);
        boundingSpherePositions.push(position.z);
      });

      // boundingSphere 생성
      boundingSphere = new BoundingSphere.fromVertices(boundingSpherePositions);
    }

    if (boundingSphere) {
      // 카메라 이동
      viewer.camera.flyToBoundingSphere(boundingSphere, {
        duration: 2,
      });
    }
  },

  /**
   * 강조할 객체 설정. <br>
   * Set objects to be emphasized.
   * @param {object} pickedObject 강조할 객체. <br> Emphasize the objects.
   */
  setHighlightObjects(
    highlightedObjects,
    hoverColor,
    selectedObject,
    pickedObjects
  ) {
    const newHighlightedObject = [];
    // 1. 기존 색상으로 변경해야될 오브젝트

    if (highlightedObjects.length > 0) {
      const newHighlightedObject = highlightedObjects.filter((beforObject) => {
        const isIncludes = pickedObjects.includes(beforObject);
        if (!isIncludes) {
          // 새로운 배열에 기존 오브젝트 없을때
          // 기존 오브젝트의 색상을 기존 색상으로 변경
          const originalColor = EditUtils.getOriginalColor(beforObject);
          if (originalColor) {
            EditUtils.changeColor(beforObject, originalColor);
          }
        } else {
          return beforObject;
        }
      });
      highlightedObjects = newHighlightedObject;
    }

    // 2. 새로운 색상으로 변경해야 될 오브젝트
    pickedObjects.forEach((newObject) => {
      const isIncludes = highlightedObjects.includes(newObject);

      if (!isIncludes && !this.isSelectedObject(newObject, selectedObject)) {
        // 기존 배열에 새로운 오브젝트 없을때
        // 새로운 오브젝트 색상을 변경
        const newColor = EditUtils.getColorByObjectType(newObject, hoverColor);

        // 기존 색상 저장
        EditUtils.setOriginalColor(newObject);
        // 색상 변경
        EditUtils.changeColor(newObject, newColor);
        newHighlightedObject.push(newObject);
      }
    });

    highlightedObjects = [...highlightedObjects, ...newHighlightedObject];

    return highlightedObjects;
  },

  /**
   * 선택 된 객체인지 확인.<br>
   * Check that it is selected Object.
   * @param {object} newObject 선택 된 객체.<br>object to check.
   * @returns {boolean} 선택 된 객체 여부. Whether the object is selected or not.(true/false)
   */
  isSelectedObject(newObject, selectedObject) {
    if (!selectedObject) {
      return false;
    }
    return (
      selectedObject === newObject ||
      selectedObject?.primitive === newObject.primitive
    );
  },

  /**
   * 3d 타일셋의 id 조회.</br>
   * Get 3DTileset Id.
   * @param {object} feature 선택된 3d타일셋.</br> The selected 3DTileset object.
   * @returns {string} 타일셋 id.</br> 3DTileset's Id.
   */
  get3DTileName(feature) {
    let i;
    const possibleIds = [];
    const propertyIds = feature.getPropertyIds();
    for (i = 0; i < propertyIds.length; i++) {
      const propertyId = propertyIds[i];
      if (/^name$/i.test(propertyId)) {
        possibleIds[0] = feature.getProperty(propertyId);
      } else if (/name/i.test(propertyId)) {
        possibleIds[1] = feature.getProperty(propertyId);
      } else if (/^title$/i.test(propertyId)) {
        possibleIds[2] = feature.getProperty(propertyId);
      } else if (/^(id|identifier)$/i.test(propertyId)) {
        possibleIds[3] = feature.getProperty(propertyId);
      } else if (/element/i.test(propertyId)) {
        possibleIds[4] = feature.getProperty(propertyId);
      } else if (/(id|identifier)$/i.test(propertyId)) {
        possibleIds[5] = feature.getProperty(propertyId);
      }
    }

    const length = possibleIds.length;
    for (i = 0; i < length; i++) {
      const item = possibleIds[i];
      if (defined(item) && item !== "") {
        return item;
      }
    }
    return "-";
    // const propertyIds = feature.getPropertyIds();
    // const possibleIds = [
    //   "name",
    //   "Name",
    //   "title",
    //   "id",
    //   "element",
    //   "identifier",
    // ];

    // for (const propertyId of propertyIds) {
    //   const index = possibleIds.findIndex((id) =>
    //     new RegExp(`^${id}$`, "i").test(propertyId)
    //   );
    //   if (index !== -1) {
    //     const item = feature.getProperty(propertyId);
    //     if (defined(item) && item !== "") {
    //       return item;
    //     }
    //   }
    // }
    // return '-';
  },
};

export default EditUtils;
